import { OrderItem } from './OrderItem';
import { useAllIngredients } from '../../state/ingredient/hooks/useAllIngredients';
import { ErrorMessage } from '../errors/ErrorMessage';
import { errorCode } from '../errors';
import { useCallback } from 'react';
import { IngredientWithDosing } from '../../components/shoppingCart/types';

export const serializeIngredients = (item: OrderItem): string => {
  return item
    .getActiveIngredients()
    .map((i) => Array(i.quantity).fill(i.ingredientID).join(';'))
    .join(';');
};

export const useDeserializeIngredients = (): {
  isLoading: boolean;
  deserializeIngredients: (
    sizeId: number,
    hash: string | null
  ) => {
    sizeId: number | null;
    ingredients: IngredientWithDosing[];
    error: ErrorMessage | null;
  };
} => {
  const { ingredients: allIngredients, isLoading } = useAllIngredients();

  const deserializeIngredients = useCallback(
    (
      sizeId: number,
      hash: string | null
    ): {
      sizeId: number | null;
      ingredients: IngredientWithDosing[];
      error: ErrorMessage | null;
    } => {
      if (!hash) {
        return {
          sizeId: null,
          ingredients: [],
          error: null,
        };
      }
      const ids = hash.split(';').map((id) => parseInt(id, 10));

      const { ingredients, error } = populateIngredients(ids, sizeId, allIngredients);

      return {
        sizeId,
        ingredients,
        error,
      };
    },
    [allIngredients]
  );

  return {
    isLoading,
    deserializeIngredients,
  };
};

export const populateIngredients = (
  ids: number[],
  sizeId: number,
  allIngredients: IngredientWithDosing[]
): {
  ingredients: IngredientWithDosing[];
  error: null;
} => {
  let error: ErrorMessage | null = null;

  const ingredients: IngredientWithDosing[] = ids
    .map((id) => {
      const ingredient = allIngredients.find(
        (i) => i.ingredientID === id && i.productSizeId === sizeId
      );

      if (!ingredient) {
        error = new ErrorMessage(errorCode.CUSTOM_ERROR, {}, 'Osaa täytteistä ei voitu lisätä.');
      }
      return ingredient;
    })
    .filter(Boolean) as IngredientWithDosing[];

  const combinedIngredients: IngredientWithDosing[] = [];

  ingredients.forEach((ingredient) => {
    const combined = combinedIngredients.find((i) => i.ingredientID == ingredient.ingredientID);

    if (combined) {
      combined.quantity += ingredient.quantity;
    } else {
      combinedIngredients.push({ ...ingredient });
    }
  });

  return {
    ingredients: combinedIngredients,
    error,
  };
};
